/**
 * Feature E03-F07 — "Internal Notification (шаблонный)" request/response
 * contracts. 1:1 port of two coexisting legacy n8n mechanisms:
 *
 *   (1) the TEMPLATE path — `get_notification_defaults` (group Notif,
 *       docs/source-n8n/reference/Notif.md + sql-queries.md "Notif: Get
 *       Defaults") returns the auto-recipients (active `staff_contacts`
 *       category='notification' ∪ the project team's teamlead) plus the two
 *       prompt templates `int_notif_standard` / `int_notif_confidential`. NO LLM
 *       — the SPA does the {{var}} substitution itself (`_applyNotifTemplate`).
 *
 *   (2) the LLM path — `generate_internal_notification` (group IN,
 *       docs/source-n8n/reference/IN.md + ai_prompts.md
 *       `generate_internal_notification`): loads the DB prompt, substitutes
 *       clientName/roleName/contactName/kickoffEnrichment, runs the model and
 *       returns `{ subject, summary, body }` (also UPSERTed as the
 *       `internal_notification` artifact).
 *
 * Single source of truth for:
 *   - GET  /projects/:id/ai/internal_notification_defaults   (template path, T03/T04-S01)
 *   - POST /projects/:id/ai/generate_internal_notification    (LLM path, T04/T04-S02)
 *   - the substitution variable set the SPA fills (T02/T05)
 *
 * IDOR invariant (carried over unchanged): no `userId` / `team_id` is part of
 * any *input* schema — the api derives the actor AND the team from the validated
 * session (Notif.md gotcha: legacy read `body.team_id` straight from the body;
 * IN.md gotcha: legacy `body.userId || 'system'`). v2 takes both from the
 * session.
 *
 * Lives in its OWN file (not re-exported from the shared barrel by this agent);
 * the serial Wire pass adds the `export *` line to `packages/shared/src/index.ts`.
 */
import { z } from 'zod';
// ---------------------------------------------------------------------------
// Template keys + artifact type (Notif.md / IN.md)
// ---------------------------------------------------------------------------
/** The two notification template prompt keys in `ai_prompts`. */
export const INT_NOTIF_PROMPT_KEYS = {
    standard: 'int_notif_standard',
    confidential: 'int_notif_confidential',
};
/**
 * KS3-03 — the "Send to Sourcing Team" covering-email template.
 *
 * Lives next to the two notification templates because it is the same KIND of
 * thing: a `{{var}}` string the app substitutes, never sent to a model. Kept in
 * `ai_prompts` so the wording is edited in Admin → Prompts, not in a deploy.
 */
export const SOURCING_TEAM_EMAIL_PROMPT_KEY = 'sourcing_team_email';
/**
 * E12-F08-T01 — the covering email for "Send" on the Sourcing Sheet card. A SEPARATE
 * row from `sourcing_team_email`: that wording talks about the kickoff sheet and the
 * kick-off date, which would be wrong next to a Sourcing Sheet.
 */
export const SOURCING_SHEET_TEAM_EMAIL_PROMPT_KEY = 'sourcing_sheet_team_email';
/** The LLM prompt key for the generate path. */
export const GENERATE_INTERNAL_NOTIFICATION_PROMPT_KEY = 'generate_internal_notification';
/** The artifact `type` the generate path UPSERTs (IN.md: UNIQUE(project_id,type)). */
export const INTERNAL_NOTIFICATION_ARTIFACT_TYPE = 'internal_notification';
/** The two selectable template variants (SPA toggle — `setNotifTemplate`). */
export const INT_NOTIF_TEMPLATE_TYPES = ['standard', 'confidential'];
export const intNotifTemplateTypeSchema = z.enum(INT_NOTIF_TEMPLATE_TYPES);
// ---------------------------------------------------------------------------
// Recipient (Notif: Return Defaults — { name, email })
// ---------------------------------------------------------------------------
/** A single notification recipient (staff or teamlead). */
export const notifRecipientSchema = z.object({
    name: z.string().default(''),
    email: z.string().default(''),
});
// ---------------------------------------------------------------------------
// Template path: GET defaults response (Notif: Return Defaults shape)
// ---------------------------------------------------------------------------
/**
 * `{ success, recipients, template_standard, template_confidential }` — the exact
 * Notif: Return Defaults envelope (Notif.md step 4). `recipients` is the ordered
 * union: staff 'notification' first, then the team's teamlead (no dedup, source
 * parity). Both templates are raw `ai_prompts.prompt_template` strings (or '').
 */
export const internalNotificationDefaultsResponseSchema = z.object({
    success: z.literal(true).optional(), // web client strips `success`; backend still sends it
    recipients: z.array(notifRecipientSchema),
    template_standard: z.string(),
    template_confidential: z.string(),
    /**
     * KS3-03 — the sourcing-team covering email. Defaults to '' so a project on a
     * database without the template row (or with it deactivated) keeps the previous
     * behaviour instead of failing the whole defaults request.
     */
    template_sourcing_team: z.string().default(''),
    /** E12-F08-T01 — the Sourcing Sheet covering email; '' when the row is absent/inactive. */
    template_sourcing_sheet: z.string().default(''),
});
// ---------------------------------------------------------------------------
// Substitution result ({ subject, body }) — _applyNotifTemplate output
// ---------------------------------------------------------------------------
/** The parsed+substituted template the SPA drops into the Subject/Body fields. */
export const notifTemplateResultSchema = z.object({
    subject: z.string(),
    body: z.string(),
});
// ---------------------------------------------------------------------------
// LLM path: generate_internal_notification ({ subject, summary, body })
// ---------------------------------------------------------------------------
/**
 * The LLM output shape (IN: Parse & Save — `{subject, summary, body}`), also the
 * `internal_notification` artifact `content`. All default to '' (the source
 * wrapped any unparseable answer as `{subject:'', summary:'', body:rawText}`).
 */
export const internalNotificationContentSchema = z.object({
    subject: z.string().default(''),
    summary: z.string().default(''),
    body: z.string().default(''),
});
/**
 * Request body. Intentionally empty of identity — `projectId` comes from the URL
 * and the actor/team from the session (IDOR rule). A `session_token` may ride in
 * the body for legacy transport parity but is never read for authorization here.
 */
export const generateInternalNotificationRequestSchema = z
    .object({
    session_token: z.string().min(1).optional(),
})
    .partial();
/**
 * Success response (IN: Parse & Save returns `{ success, content, projectId }`;
 * the SPA reads `content` as `{subject, summary, body}`).
 */
export const generateInternalNotificationResponseSchema = z.object({
    success: z.literal(true).optional(), // web client strips `success`; backend still sends it
    projectId: z.number().int(),
    content: internalNotificationContentSchema,
});
//# sourceMappingURL=internal-notification.js.map